import { reactive, computed } from "vue";
import { getPayPlanVersionDetailV2, voidPayPlanVersionV2 } from "@/api/employeeAPI";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { apiErrorMessage } from "@components/ProposedPayPlanDrawer/composables/useProposalDrawer";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";
import {
  mapFairPayAtEffectiveDate,
  mapVersionDetail,
} from "@components/CurrentPayPlanDrawer/composables/useCurrentPayPlanDrawer";

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

/**
 * The pane's calculator reads `readinessAsOf`, `pay_range_adjusted` and
 * `fte_adjusted`; the Current drawer's mapper names the same data `adjusted_band`
 * / `fte_adjustment_applied`. Widen the shared shape for the pane.
 */
export function mapPaneFairPay(block, fallbackCountryCode = null) {
  const base = mapFairPayAtEffectiveDate(block, fallbackCountryCode);
  if (!base) return null;
  return {
    ...base,
    readinessAsOf: base.pay_range_version?.asOfDate || null,
    pay_range_adjusted: base.adjusted_band || null,
    fte_adjusted: block?.fte_adjustment_applied === true,
  };
}

/**
 * Wiring for the PayPlanVersionPane (ER-12119).
 *
 * Two read-only scenarios off the same ER-12115 detail:
 * - `future-scheduled` — an In Force version whose period starts in the future.
 * - `voided` — a VOIDED version: no actions, "never valid" banner.
 *
 * Void is the only mutation (Rule A-8/A-9, flag-gated); Propose Changes is
 * always disabled on a future version and is bubbled for the page to handle.
 */
export function usePayPlanVersionPane({ proxy, fetchRows } = {}) {
  const state = reactive({
    open: false,
    loading: false,
    submitting: false,
    error: null,
    accountId: null,
    employeeId: null,
    versionId: null,
    detail: null,
    employee: null,
    mutationEnabled: false,
    voidOpen: false,
    voidReason: "",
  });

  const scenario = computed(() => {
    if (!state.detail) return "loading";
    return String(state.detail.status || "").toUpperCase() === "VOIDED"
      ? "voided"
      : "future-scheduled";
  });

  const mapped = computed(() => mapVersionDetail(state.detail, state.employee));
  const version = computed(() => mapped.value.version);
  const fairPayAtEffectiveDate = computed(() => {
    if (!state.detail) return null;
    return mapPaneFairPay(
      state.detail.fair_pay_at_effective_date,
      state.detail.country?.code || null
    );
  });

  /**
   * Readiness gate — same rule as the Current drawer: the detail's
   * `readiness_enabled` is the subscription flag (default off) while the app
   * gates Readiness on the account product flag (default on).
   */
  const readinessEnabled = computed(
    () => isProductReadinessEnabled() || state.detail?.readiness_enabled === true
  );
  const voidable = computed(() => state.detail?.voidable === true);
  const voidBlockedBy = computed(() => state.detail?.void_blocked_by || "");
  const featureDisabled = computed(() => !state.mutationEnabled);

  function notify(type, message) {
    const notifier = proxy?.$notifications?.[type];
    if (typeof notifier === "function") {
      notifier(message);
    } else if (type === "error") {
      state.error = message;
    }
  }

  function setInlineError(error, fallback) {
    state.error = apiErrorMessage(error, fallback);
  }

  async function ensureMutationEnabled() {
    try {
      state.mutationEnabled =
        (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
    } catch {
      state.mutationEnabled = false;
    }
    return state.mutationEnabled;
  }

  async function loadDetail(versionId) {
    if (!state.accountId || versionId == null) return;
    state.loading = true;
    state.error = null;
    try {
      const response = await getPayPlanVersionDetailV2(state.accountId, versionId);
      state.detail = response?.data || null;
    } catch (error) {
      state.detail = null;
      setInlineError(error, "Could not load the pay plan version");
    } finally {
      state.loading = false;
    }
  }

  function open({ accountId, employeeId, versionId, employee } = {}) {
    state.open = true;
    state.error = null;
    state.accountId = accountId || getAccountId();
    state.employeeId = employeeId || null;
    state.versionId = versionId ?? null;
    state.detail = null;
    state.employee = employee || null;
    state.voidOpen = false;
    state.voidReason = "";
    ensureMutationEnabled();
    loadDetail(state.versionId);
  }

  function close() {
    state.open = false;
    state.error = null;
    state.detail = null;
    state.employee = null;
    state.voidOpen = false;
    state.voidReason = "";
  }

  function openVoid() {
    state.voidReason = "";
    state.voidOpen = true;
  }

  function cancelVoid() {
    state.voidOpen = false;
    state.voidReason = "";
  }

  async function refresh() {
    if (fetchRows) await fetchRows();
  }

  async function voidVersion() {
    const reason = state.voidReason.trim();
    if (!reason) return null;

    if (!state.mutationEnabled && !(await ensureMutationEnabled())) {
      notify("error", "Fair Pay pay plan actions are not available");
      return null;
    }

    state.submitting = true;
    state.error = null;
    try {
      await voidPayPlanVersionV2(state.accountId, state.versionId, { reason });
      notify(
        "success",
        `${version.value?.code || "Pay plan version"} voided`
      );
      state.voidOpen = false;
      close();
      await refresh();
      return true;
    } catch (error) {
      setInlineError(error, "Could not void the pay plan version");
      notify("error", apiErrorMessage(error, "Could not void the pay plan version"));
      return null;
    } finally {
      state.submitting = false;
    }
  }

  return {
    state,
    scenario,
    version,
    fairPayAtEffectiveDate,
    readinessEnabled,
    voidable,
    voidBlockedBy,
    featureDisabled,
    open,
    close,
    openVoid,
    cancelVoid,
    voidVersion,
  };
}
